import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Avatar, Dropdown, Typography, Badge } from 'antd';
import {
  MenuOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import TenantSelector from '../common/TenantSelector';
import ThemeToggle from '../common/ThemeToggle';

const { Text } = Typography;

// An opaque chrome bar — distinct from both page and rail — on a hairline with a
// soft drop. No backdrop-filter: the walk-round fleet is Android tablets.
const HeaderContainer = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 ${({ theme }) => theme.spacing.lg};
  height: ${({ theme }) => theme.layout.headerHeight};
  background: ${({ theme }) => theme.colors.chrome};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.chrome};
  box-sizing: border-box;
  transition: all 0.3s ease;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  ${({ theme }) => theme.mediaQuery.maxMd} {
    /* Defense-in-depth: HeaderContainer's flex row has no flex-wrap, so let
       this flex child shrink below its content's natural min-width instead
       of forcing the row wider than the viewport. */
    min-width: 0;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: 20px;
  transition: all 0.3s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.hover};
    color: ${({ theme }) => theme.colors.text};
  }

  ${({ theme }) => theme.mediaQuery.maxMd} {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};

  ${({ theme }) => theme.mediaQuery.maxMd} {
    /* See LeftSection above. */
    min-width: 0;
  }
`;

const NotificationButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  /* Neutral hover: the accent is never spent on a hover in the shell */
  &:hover {
    background: ${({ theme }) => theme.colors.hover};
    color: ${({ theme }) => theme.colors.text};
  }
`;

// User section: a chrome pill on a hairline; the hover is neutral
const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  padding: 6px 8px 6px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.chrome};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderHover};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  ${({ theme }) => theme.mediaQuery.maxSm} {
    display: none;
  }
`;

const UserName = styled(Text)`
  color: ${({ theme }) => theme.colors.text};
  font-family: ${({ theme }) => theme.typography.fontFamily.heading};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  font-size: 13px;
  line-height: 1.2;
`;

const UserRole = styled(Text)`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 11.5px;
  line-height: 1.2;
`;

const StyledAvatar = styled(Avatar)`
  background: ${({ theme }) => theme.colors.accent};
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const DashboardHeader = ({ onToggleMobileMenu }) => {
  const { user, logout, isAuthenticated } = useAuth();

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Sign Out',
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === 'logout') {
      logout();
    }
    // Handle other menu items as needed
  };

  const getUserInitials = () => {
    if (!user?.name) return '?';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  return (
    <HeaderContainer data-testid="dashboard-header">
      <LeftSection>
        <MobileMenuButton onClick={onToggleMobileMenu}>
          <MenuOutlined />
        </MobileMenuButton>
        <TenantSelector />
      </LeftSection>

      <RightSection>
        {/* Theme Toggle */}
        <ThemeToggle />

        <NotificationButton>
          <Badge count={0} size="small">
            <BellOutlined style={{ fontSize: 18, color: 'inherit' }} />
          </Badge>
        </NotificationButton>

        {isAuthenticated && user && (
          <Dropdown
            menu={{ items: userMenuItems, onClick: handleMenuClick }}
            trigger={['click']}
            placement="bottomRight"
          >
            <UserSection>
              <UserInfo>
                <UserName>{user.name || 'User'}</UserName>
                <UserRole>{user.email || ''}</UserRole>
              </UserInfo>
              <StyledAvatar size="default">
                {getUserInitials()}
              </StyledAvatar>
            </UserSection>
          </Dropdown>
        )}
      </RightSection>
    </HeaderContainer>
  );
};

DashboardHeader.propTypes = {
  onToggleMobileMenu: PropTypes.func.isRequired,
};

export default DashboardHeader;
